// Where a member's key material lives on the device between launches (spec
// section 6.2): one record per member id in IndexedDB, holding MK, its keyId,
// this install's device id and ECDH key pair. Records are keyed by member id so
// two members sharing a phone never see each other's keys, and logout removes
// only the signing-out member's record. IndexedDB is the browser's only store
// that keeps a non-extractable CryptoKey; when it is missing or refuses to
// open (a locked-down WebView, private browsing) the store degrades to memory
// for the session and says so, rather than failing every read.
//
// A SECOND OBJECT STORE holds the provisional enrolment attempt (section 6.7,
// decision 40): everything needed to re-send the identical `POST
// /api/me/encryption` after a restart, kept apart from the key record so that
// no getter that answers "does this device hold the key" can ever see it. The
// promotion from attempt to key record is ONE transaction over both stores —
// `committed` is neither a flag nor a derivation, so no later wipe can misread
// one for the other.

import { assertKeyId } from './identifiers';
import { assertMasterKey } from './master-key';

export const KEY_STORE_DB_NAME = 'hogc-one-courses-keys';
/** 2 added the `attempts` store; the bump is additive and migrates no data. */
export const KEY_STORE_VERSION = 2;
const KEYS_STORE = 'keys';
const ATTEMPTS_STORE = 'attempts';
const OBJECT_STORES = [KEYS_STORE, ATTEMPTS_STORE];

/**
 * What an enrolment attempt can be, as a stored field rather than an
 * inference. `prepared → ambiguous` is the only edge that sets `ambiguous`
 * and there is no edge back: once a send is recorded, every later answer is
 * reconciled against a status read before it is interpreted.
 */
export const ATTEMPT_STATE = Object.freeze({
  /** Written and read back; nothing sent. A blind send is allowed from here only. */
  PREPARED: 'prepared',
  /** The send was durably recorded; the POST may or may not have committed. */
  AMBIGUOUS: 'ambiguous',
  /** The API refused the body (400). Kept, not retried; discarded only on proof. */
  REJECTED: 'rejected',
});
const ATTEMPT_STATES = Object.values(ATTEMPT_STATE);

/**
 * What a wipe of the store forgets for a member. Here rather than in the
 * store module because the reset path names a scope and must stay clear of
 * that module's native imports.
 */
export const WIPE_SCOPE = Object.freeze({
  /** Sign-out: the key record and the attempt row. MK leaves IndexedDB entirely. */
  LOGOUT: 'logout',
  /**
   * Start over, and the reconciliation of a completed hold: the key record
   * only. Those paths know that the HELD key is dead, not that an in-flight
   * attempt is — only the enrolment reconcile may discard an attempt, with
   * that attempt's own keyId in hand.
   */
  RESET: 'reset',
});

/**
 * @typedef {object} KeyRecord
 * @property {number} memberId
 * @property {Uint8Array} mk
 * @property {string} keyId
 * @property {string | null} deviceId
 * @property {string | null} devicePublicKey base64 SPKI
 * @property {CryptoKey | null} devicePrivateKey
 * @property {Uint8Array | null} [approvalSecret] the out-of-band approval
 *   secret S (#1693). Optional on purpose: a record written before Phase 2b
 *   has none, reads back `undefined`, and is back-filled by
 *   `device-approval.js`'s `ensureLocalIdentity`. IndexedDB object stores are
 *   schemaless, so an added field needs no migration and KEY_STORE_VERSION
 *   does not move.
 * @property {{ keyId: string, mk: Uint8Array } | null} [pending] the NEXT
 *   generation, held between a rotation's begin and its commit (#1694, section
 *   6.12). Two generations have to coexist on the rotating device: MK1 still
 *   opens every row that has not been re-sealed yet, and MK2 is what the
 *   re-sealing writes under. Optional for the same reason as `approvalSecret`,
 *   and added the same way — a schemaless store needs no migration, and
 *   KEY_STORE_VERSION does not move for a field. (The alternative, a version
 *   bump, would put every installed device through `onupgradeneeded` to add a
 *   field that reads back as `undefined` anyway.)
 */

/**
 * @typedef {object} AttemptRecord
 * @property {number} memberId
 * @property {'prepared' | 'ambiguous' | 'rejected'} state
 * @property {string} keyId
 * @property {Uint8Array} mk
 * @property {string} deviceId
 * @property {string} devicePublicKey base64 SPKI
 * @property {CryptoKey} devicePrivateKey
 * @property {string} signingKey the bundle's public signing key, base64 SPKI
 * @property {{ iv: string, wrapped: string }} signingKeyWrapped
 * @property {number} deviceIdRetries how many fresh device ids this attempt has minted
 * @property {number} preparedAt telemetry only; no rule reads a clock
 * @property {number | null} postedAt
 * @property {number | null} rejectedAt
 */

/**
 * @typedef {object} KeyStore
 * @property {boolean} persistent false when keys live only for this session
 * @property {(memberId: number) => Promise<KeyRecord | null>} get
 * @property {(record: KeyRecord) => Promise<void>} put
 * @property {(memberId: number) => Promise<void>} remove
 * @property {(memberId: number) => Promise<AttemptRecord | null>} getAttempt
 * @property {(attempt: AttemptRecord) => Promise<void>} putAttempt
 * @property {(memberId: number) => Promise<void>} removeAttempt
 * @property {(record: KeyRecord) => Promise<void>} promote writes the key record
 *   and deletes the member's attempt in one transaction
 * @property {() => void} close releases the connection; the next call reopens it
 */

/**
 * @param {unknown} memberId
 */
function assertMemberId(memberId) {
  if (!Number.isInteger(memberId) || memberId <= 0) {
    throw new TypeError('memberId must be a positive integer');
  }
}

/**
 * A record may hold no master key yet — a device identity registered
 * `PENDING`, waiting for another device to approve it, is a record without
 * MK — but a record that claims a key must hold a real one. A wrong-length
 * buffer or a string where the bytes should be is a corrupted record, and
 * writing it would later read as "this device holds the key".
 *
 * @param {unknown} record
 */
function assertRecord(record) {
  if (record === null || typeof record !== 'object') {
    throw new TypeError('record must be an object');
  }
  assertMemberId(record.memberId);
  if (record.mk !== null && record.mk !== undefined) assertMasterKey(record.mk);
  assertPending(record.pending);
}

/**
 * A pending generation is optional, but one that claims a key must hold a real
 * one — the same rule `mk` is held to, and for a sharper reason (#1694).
 *
 * A half-written `pending` reads back as "this device can continue the
 * rotation". The rotating device would then re-seal rows under a key that is
 * not 32 bytes of anything, and the rows it wrote would open for nobody,
 * including itself. That is worse than a failed rotation: a failed rotation is
 * resumable, and rows sealed under nonsense are not.
 *
 * @param {unknown} pending
 */
function assertPending(pending) {
  if (pending === null || pending === undefined) return;
  if (typeof pending !== 'object') throw new TypeError('record.pending must be an object or null');
  assertKeyId(pending.keyId);
  assertMasterKey(pending.mk);
}

/**
 * An attempt is only worth keeping if it can be re-sent as the SAME attempt:
 * the match rule the API replays on is `keyId` + `device.id` + `signingKey`,
 * so all three, the wrapping that goes with them, and a state this module
 * knows how to resume from.
 *
 * @param {unknown} attempt
 */
function assertAttempt(attempt) {
  if (attempt === null || typeof attempt !== 'object') {
    throw new TypeError('attempt must be an object');
  }
  assertMemberId(attempt.memberId);
  if (!ATTEMPT_STATES.includes(attempt.state)) {
    throw new TypeError(`attempt.state must be one of ${ATTEMPT_STATES.join(', ')}`);
  }
  assertKeyId(attempt.keyId);
  assertMasterKey(attempt.mk);
  if (typeof attempt.deviceId !== 'string' || !attempt.deviceId) {
    throw new TypeError('attempt.deviceId must be a non-empty string');
  }
  if (typeof attempt.signingKey !== 'string' || !attempt.signingKey) {
    throw new TypeError('attempt.signingKey must be a non-empty string');
  }
  const wrapping = attempt.signingKeyWrapped;
  if (
    !wrapping ||
    typeof wrapping !== 'object' ||
    typeof wrapping.iv !== 'string' ||
    typeof wrapping.wrapped !== 'string'
  ) {
    throw new TypeError('attempt.signingKeyWrapped must carry iv and wrapped');
  }
}

/**
 * A copy that shares no byte buffer with the caller, so zeroing one copy of MK
 * (on wipe) cannot reach the other.
 *
 * S is cloned for exactly the same reason (#1693): `courses-keys/wipe` zeroes
 * the copy it holds in memory, and a shared buffer would carry that zeroing
 * back into the record the memory store is keeping — which on the memory
 * fallback is the record itself. Written as a conditional add rather than a
 * field on every clone because this helper also clones ATTEMPT rows, which
 * have no S, and giving them an `approvalSecret: undefined` would put a field
 * on the wire that nothing reads.
 *
 * @template {{ mk?: Uint8Array | null, approvalSecret?: Uint8Array | null }} T
 * @param {T} record
 * @returns {T}
 */
function cloneRecord(record) {
  const clone = { ...record, mk: record.mk ? new Uint8Array(record.mk) : record.mk };
  if (record.approvalSecret) clone.approvalSecret = new Uint8Array(record.approvalSecret);
  // The pending generation's MK needs its own buffer for the same reason the
  // committed one does, and the spread above does NOT give it one: it copies
  // the `pending` object by reference, so `pending.mk` would still be the
  // caller's buffer. A wipe zeroing one would reach the other (#1694).
  if (record.pending) {
    clone.pending = { ...record.pending, mk: new Uint8Array(record.pending.mk) };
  }
  return clone;
}

/**
 * Commit's local half: MK2 becomes the committed generation and MK1 is gone
 * (#1694, section 6.12).
 *
 * Returns null when there is nothing to promote, and the callers treat that as
 * "no change" rather than an error: a commit arriving twice — a retry, a second
 * tab — must be a no-op, not a throw, and a device that never began a rotation
 * has nothing to forget.
 *
 * @param {KeyRecord | null} record
 * @returns {KeyRecord | null}
 */
function rewritePromoted(record) {
  if (!record?.pending) return null;
  return { ...record, mk: record.pending.mk, keyId: record.pending.keyId, pending: null };
}

/**
 * Abort's local half: the pending generation is forgotten and the committed one
 * is untouched, byte for byte.
 *
 * @param {KeyRecord | null} record
 * @returns {KeyRecord | null}
 */
function rewriteDropped(record) {
  if (!record?.pending) return null;
  return { ...record, pending: null };
}

/**
 * @param {Map<number, KeyRecord>} records
 * @param {number} memberId
 * @param {(record: KeyRecord | null) => KeyRecord | null} rewrite
 * @returns {Promise<KeyRecord | null>}
 */
function rewriteMemoryRecord(records, memberId, rewrite) {
  assertMemberId(memberId);
  const next = rewrite(records.get(memberId) ?? null);
  if (!next) return null;
  assertRecord(next);
  records.set(memberId, cloneRecord(next));
  return cloneRecord(next);
}

/**
 * @returns {KeyStore} an in-memory store: for specs, and the fallback when
 *   IndexedDB is unavailable
 */
export function createMemoryKeyStore() {
  const records = new Map();
  const attempts = new Map();
  return {
    persistent: false,
    async get(memberId) {
      assertMemberId(memberId);
      const record = records.get(memberId);
      return record ? cloneRecord(record) : null;
    },
    async put(record) {
      assertRecord(record);
      records.set(record.memberId, cloneRecord(record));
    },
    async remove(memberId) {
      assertMemberId(memberId);
      records.delete(memberId);
    },
    async getAttempt(memberId) {
      assertMemberId(memberId);
      const attempt = attempts.get(memberId);
      return attempt ? cloneRecord(attempt) : null;
    },
    async putAttempt(attempt) {
      assertAttempt(attempt);
      attempts.set(attempt.memberId, cloneRecord(attempt));
    },
    async removeAttempt(memberId) {
      assertMemberId(memberId);
      attempts.delete(memberId);
    },
    async promote(record) {
      assertRecord(record);
      records.set(record.memberId, cloneRecord(record));
      attempts.delete(record.memberId);
    },
    async promoteGeneration(memberId) {
      return rewriteMemoryRecord(records, memberId, rewritePromoted);
    },
    async dropPending(memberId) {
      return rewriteMemoryRecord(records, memberId, rewriteDropped);
    },
    close() {},
  };
}

/**
 * Opens the database, creating whichever object stores are missing. A
 * database a version-1 build created has only `keys`; the upgrade adds
 * `attempts` beside it and touches nothing else. A `blocked` event means
 * another tab still holds an older version; that tab closes its connection
 * on `versionchange` (set below), after which `success` follows, so blocked
 * is logged and waited out rather than treated as a failure.
 *
 * @param {string} name
 * @returns {Promise<IDBDatabase>}
 */
function openDatabase(name) {
  return new Promise((resolve, reject) => {
    const request = globalThis.indexedDB.open(name, KEY_STORE_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      OBJECT_STORES.forEach(storeName => {
        if (!db.objectStoreNames.contains(storeName)) {
          db.createObjectStore(storeName, { keyPath: 'memberId' });
        }
      });
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('IndexedDB open failed'));
    request.onblocked = () => {
      console.warn('[CoursesKeys] IndexedDB open is blocked by another connection; waiting');
    };
  });
}

/**
 * Runs one transaction over the named stores. Writes resolve on transaction
 * completion, not request success, so a caller that reads straight after a
 * put sees the committed record — and so that two writes in one transaction
 * either both land or neither does.
 *
 * @param {IDBDatabase} db
 * @param {'readonly' | 'readwrite'} mode
 * @param {string | string[]} storeNames
 * @param {(transaction: IDBTransaction) => IDBRequest | undefined} operate
 *   issues the requests; the returned one's result is what resolves
 * @returns {Promise<unknown>}
 */
function runTransaction(db, mode, storeNames, operate) {
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(storeNames, mode);
    const fail = error => reject(error ?? new Error('IndexedDB request failed'));
    transaction.onerror = () => fail(transaction.error);
    transaction.onabort = () => fail(transaction.error);
    let request;
    try {
      request = operate(transaction);
    } catch (error) {
      // A request IndexedDB refuses to queue (a value it cannot clone) throws
      // here rather than erroring later, and the requests queued before it
      // would otherwise auto-commit on their own — half a promotion.
      transaction.abort();
      fail(error);
      return;
    }
    if (request) request.onerror = () => fail(request.error);
    if (mode === 'readonly') {
      request.onsuccess = () => resolve(request.result);
    } else {
      transaction.oncomplete = () => resolve(request?.result);
    }
  });
}

/**
 * @param {{ name?: string }} [options]
 * @returns {Promise<KeyStore>}
 */
export async function createIndexedDbKeyStore({ name = KEY_STORE_DB_NAME } = {}) {
  let db = null;
  const connect = async () => {
    if (db) return db;
    const opened = await openDatabase(name);
    // A later release bumping KEY_STORE_VERSION must not be blocked by this
    // connection, and a connection the browser closed behind our back must
    // not be handed out again: drop it and let the next call reopen.
    opened.onversionchange = () => {
      opened.close();
      if (db === opened) db = null;
    };
    opened.onclose = () => {
      if (db === opened) db = null;
    };
    db = opened;
    return db;
  };
  const read = async (storeName, memberId) => {
    assertMemberId(memberId);
    const record = await runTransaction(await connect(), 'readonly', storeName, transaction =>
      transaction.objectStore(storeName).get(memberId),
    );
    return record ?? null;
  };
  const write = async (storeName, record) =>
    runTransaction(await connect(), 'readwrite', storeName, transaction =>
      transaction.objectStore(storeName).put(record),
    );
  /**
   * Read, rewrite and write back in ONE transaction (#1694).
   *
   * THE WHOLE POINT IS THE SINGLE TRANSACTION. A promotion read in one and
   * written in another is a window in which a second tab, or this tab's own
   * `wipe`, can land between them — and the record that gets written back is
   * then computed from a state that no longer exists. Half a promotion is a
   * device that has forgotten MK1 without adopting MK2: every row it holds
   * becomes unreadable by it, and nothing local can put that right.
   *
   * The rewrite runs inside the `get`'s own `onsuccess`, which is still within
   * the transaction's lifetime, so the `put` joins it rather than opening a new
   * one. A rewrite that throws aborts explicitly: `runTransaction` only guards
   * the synchronous `operate` call, and a throw in this callback would
   * otherwise let the transaction auto-commit with the get having happened and
   * no put — harmless today, but exactly the shape that becomes a half-write
   * the moment a second request is added here.
   *
   * @param {number} memberId
   * @param {(record: KeyRecord | null) => KeyRecord | null} rewrite returns
   *   null for "nothing to do", which is not an error: a commit arriving twice
   *   must be a no-op.
   * @returns {Promise<KeyRecord | null>} the record as written, or null
   */
  const rewriteRecord = async (memberId, rewrite) => {
    assertMemberId(memberId);
    let written = null;
    await runTransaction(await connect(), 'readwrite', KEYS_STORE, transaction => {
      const store = transaction.objectStore(KEYS_STORE);
      const get = store.get(memberId);
      get.onsuccess = () => {
        try {
          const next = rewrite(get.result ?? null);
          if (!next) return;
          assertRecord(next);
          written = next;
          store.put(next);
        } catch (error) {
          written = null;
          transaction.abort();
        }
      };
      return get;
    });
    return written ? cloneRecord(written) : null;
  };
  const erase = async (storeName, memberId) => {
    assertMemberId(memberId);
    await runTransaction(await connect(), 'readwrite', storeName, transaction =>
      transaction.objectStore(storeName).delete(memberId),
    );
  };
  await connect();
  return {
    persistent: true,
    get: memberId => read(KEYS_STORE, memberId),
    async put(record) {
      assertRecord(record);
      await write(KEYS_STORE, record);
    },
    remove: memberId => erase(KEYS_STORE, memberId),
    getAttempt: memberId => read(ATTEMPTS_STORE, memberId),
    async putAttempt(attempt) {
      assertAttempt(attempt);
      await write(ATTEMPTS_STORE, attempt);
    },
    removeAttempt: memberId => erase(ATTEMPTS_STORE, memberId),
    async promote(record) {
      assertRecord(record);
      await runTransaction(await connect(), 'readwrite', OBJECT_STORES, transaction => {
        // The put first: it is the request that can be refused outright.
        const put = transaction.objectStore(KEYS_STORE).put(record);
        transaction.objectStore(ATTEMPTS_STORE).delete(record.memberId);
        return put;
      });
    },
    promoteGeneration: memberId => rewriteRecord(memberId, rewritePromoted),
    dropPending: memberId => rewriteRecord(memberId, rewriteDropped),
    close() {
      if (db) db.close();
      db = null;
    },
  };
}

/**
 * The store the app uses: IndexedDB when it exists and opens, memory otherwise.
 *
 * @param {{ name?: string }} [options]
 * @returns {Promise<KeyStore>}
 */
export async function openKeyStore(options = {}) {
  if (!globalThis.indexedDB) return createMemoryKeyStore();
  try {
    return await createIndexedDbKeyStore(options);
  } catch (error) {
    console.warn('[CoursesKeys] IndexedDB unavailable; keys live for this session only:', error);
    return createMemoryKeyStore();
  }
}
